import React from "react";
import { KEY_ARROW_DOWN, KEY_ARROW_LEFT, KEY_ARROW_RIGHT, KEY_ARROW_UP } from "../../../../../shared/frontend/ui/keyboardKeys";
import { Button, Tooltip } from "../../../../../shared/frontend/ui";

export type OutcomeTileOption<V extends string | number = number> = {
  label: React.ReactNode;
  value: V;
  icon?: React.ReactNode;
  tone?: "success" | "warning" | "danger";
  /** Nicht wählbar (z. B. Kanal ohne gültige Voraussetzung); Grund über tooltip erklären. */
  disabled?: boolean;
  tooltip?: React.ReactNode;
};

/**
 * Farbige Ergebnis-Kachel-Auswahl (Form.Item-kompatibel via value/onChange).
 * Gemeinsames Gerüst für Terminabschluss (Kalender, numerische Werte) und
 * Kontakt-Abschluss (Leads, Abschlussgrund-IDs). Optik zentral über die
 * mu-outcome-tile-*-Klassen in styles/global.css.
 * A11y: Radiogroup-Semantik — eine Auswahl aus mehreren Optionen, Pfeiltasten wechseln.
 */
export function OutcomeTileSelector<V extends string | number = number>({
  value,
  onChange,
  options,
  emptyValue,
  allowClear = false,
  onClear,
  twoColumns = false
}: {
  value?: V | null;
  onChange?: (value: V) => void;
  options: OutcomeTileOption<V>[];
  /** Wert, der als „nichts ausgewählt" gilt (z. B. 0 beim Terminabschluss). Ohne Angabe ist bei leerem value keine Kachel aktiv. */
  emptyValue?: V;
  /** Erlaubt bei optionalen Feldern, die aktive Kachel durch erneutes Anklicken abzuwählen. */
  allowClear?: boolean;
  onClear?: () => void;
  /** Für lange Beschriftungen: zwei Kacheln je Zeile. */
  twoColumns?: boolean;
}) {
  const selectedKey = value != null ? String(value) : emptyValue != null ? String(emptyValue) : null;
  const selectedIndex = selectedKey != null ? options.findIndex((o) => String(o.value) === selectedKey) : -1;

  const moveSelection = (event: React.KeyboardEvent<HTMLDivElement>, delta: number) => {
    if (options.length === 0 || options.every((o) => o.disabled)) return;
    event.preventDefault();
    const from = selectedIndex >= 0 ? selectedIndex : delta > 0 ? -1 : 0;
    let next = from;
    do {
      next = ((next + delta) % options.length + options.length) % options.length;
    } while (options[next]!.disabled && next !== from);
    if (options[next]!.disabled) return;
    onChange?.(options[next]!.value);
    const buttons = event.currentTarget.querySelectorAll<HTMLButtonElement>("button");
    buttons[next]?.focus();
  };

  return (
    <div
      className={`mu-outcome-tile-grid${twoColumns ? " mu-outcome-tile-grid--two" : ""}`}
      role="radiogroup"
      onKeyDown={(event) => {
        if (event.key === KEY_ARROW_RIGHT || event.key === KEY_ARROW_DOWN) moveSelection(event, 1);
        else if (event.key === KEY_ARROW_LEFT || event.key === KEY_ARROW_UP) moveSelection(event, -1);
      }}
    >
      {options.map((option) => {
        const selected = selectedKey != null && String(option.value) === selectedKey;
        const tile = (
          <Button
            role="radio"
            aria-checked={selected}
            type={selected ? "primary" : "default"}
            disabled={option.disabled}
            className={`mu-outcome-tile-card mu-outcome-tile-card-${option.tone ?? "default"}`}
            icon={option.icon ? <span className="mu-outcome-tile-icon">{option.icon}</span> : undefined}
            onClick={() => {
              if (option.disabled) return;
              if (selected && allowClear) return onClear?.();
              else return onChange?.(option.value);
            }}
          >
            <span>{option.label}</span>
          </Button>
        );
        return option.tooltip ? (
          <Tooltip key={String(option.value)} title={option.tooltip}>
            {/* span, damit der Tooltip auch auf deaktivierten Buttons auslöst */}
            <span className="mu-outcome-tile-tooltip-target">{tile}</span>
          </Tooltip>
        ) : (
          <React.Fragment key={String(option.value)}>{tile}</React.Fragment>
        );
      })}
    </div>
  );
}
